import React, { useEffect, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RefreshCw, Loader2 } from "lucide-react";
import Markdown from "../../components/Markdown";

export default function QuizTab({ cls, subject, chapterId }) {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const load = () => {
    setLoading(true); setSubmitted(false); setAnswers({}); setQuiz(null);
    api.get(`/quiz/${cls}/${subject}/${chapterId}`)
      .then((r) => setQuiz(r.data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cls, subject, chapterId]);

  if (loading) return (
    <div className="brut-card p-8 mt-4 bg-white flex items-center gap-3">
      <Loader2 className="animate-spin" strokeWidth={2.5}/>
      <span className="font-bold">Generating a fresh 10-question quiz…</span>
    </div>
  );
  if (!quiz) return null;

  const score = Object.entries(answers).reduce((s, [i, a]) => s + (a === quiz.questions[i].answer_index ? 1 : 0), 0);

  return (
    <div className="brut-card p-6 mt-4 bg-white" data-testid="quiz-content">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase">Chapter Quiz — 10 Questions</div>
          <div className="text-lg font-bold">{quiz.title}</div>
        </div>
        <Button onClick={load} data-testid="new-quiz-btn"
          className="brut-btn rounded-none bg-[#FFF5D1] text-black hover:bg-[#FFF5D1]">
          <RefreshCw size={14} className="mr-1" strokeWidth={2.5}/> New Quiz
        </Button>
      </div>

      {submitted && (
        <div className="brut-card p-4 bg-[#E8F3EC] mb-6" data-testid="quiz-score">
          <div className="text-2xl font-black">Score: {score} / {quiz.questions.length}</div>
          <div className="text-sm mt-1">
            {score >= 8 ? "Excellent! 🎉" : score >= 5 ? "Good — review the misses below." : "Keep practising — read the notes tab again."}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {quiz.questions.map((q, i) => (
          <div key={i} className="brut-border p-4 bg-white" data-testid={`q-${i}`}>
            <div className="font-bold mb-2">
              <span className="mr-2">{i + 1}.</span>
              <Markdown className="inline">{q.q}</Markdown>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {q.options.map((opt, j) => {
                const chosen = answers[i] === j;
                const correct = submitted && j === q.answer_index;
                const wrong = submitted && chosen && j !== q.answer_index;
                return (
                  <button key={j}
                    disabled={submitted}
                    onClick={() => setAnswers({ ...answers, [i]: j })}
                    data-testid={`q-${i}-opt-${j}`}
                    className={`text-left brut-border brut-shadow-sm p-2 font-semibold transition-transform hover:-translate-y-0.5 disabled:hover:translate-y-0
                      ${correct ? "bg-[#E8F3EC]" : wrong ? "bg-[#FFB3BA]" : chosen ? "bg-[#FFF5D1]" : "bg-white"}`}>
                    <span className="mr-2 font-black">{String.fromCharCode(65 + j)}.</span>
                    <Markdown className="inline">{opt}</Markdown>
                    {submitted && correct && <CheckCircle2 size={16} strokeWidth={2.5} className="inline ml-2"/>}
                    {submitted && wrong && <XCircle size={16} strokeWidth={2.5} className="inline ml-2"/>}
                  </button>
                );
              })}
            </div>
            {submitted && q.explanation && (
              <div className="mt-2 text-sm bg-[#FFF5D1] brut-border p-2">
                <b>Explanation: </b>
                <Markdown className="inline">{q.explanation}</Markdown>
              </div>
            )}
          </div>
        ))}
      </div>

      {!submitted && (
        <Button onClick={() => setSubmitted(true)} data-testid="submit-quiz-btn"
          className="brut-btn rounded-none w-full mt-6 bg-[#1A1A1A] text-white hover:bg-[#1A1A1A] py-6 text-base">
          Submit Quiz
        </Button>
      )}
    </div>
  );
}
